import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/useAuth';
import { getStoredToken, apiPost, apiFetch, apiGet } from '../api';
import { useNotification } from '../context/NotificationContext';
import { useWebSocket } from '../context/WebSocketContext';

const SessionRevokedModal = () => {
    const { token, user, sid, logout } = useAuth();
    const { addNotification } = useNotification();

    // ── SSA / SESSION_REVOKED state (unchanged) ─────────────────────────────
    const [isOpen, setIsOpen] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [maxTimeout, setMaxTimeout] = useState(30);
    const [ssaRequestId, setSsaRequestId] = useState(null);
    const [ssaDeviceInfo, setSsaDeviceInfo] = useState('');
    const [ssaIpAddress, setSsaIpAddress] = useState('');
    const [ssaCountry, setSsaCountry] = useState('');
    const [ssaCity, setSsaCity] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [modalType, setModalType] = useState('SSA_REQUEST');

    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);

    // ── WebSocket — via Global Provider (no local socket) ──────────────────
    // The global WebSocketProvider manages a single persistent connection.
    // We simply subscribe to the relevant message types here.
    const { subscribe } = useWebSocket();

    useEffect(() => {
        if (!token || !user) {
            setIsOpen(false);
            return;
        }

        const unsubSSA = subscribe('SSA_LOGIN_REQUEST', (payload) => {
            // Delay 500ms — fast WS may arrive before the HTTP POST writes to sessionStorage.
            setTimeout(() => {
                const pendingRequestId = sessionStorage.getItem('ssa_pending_request');
                if (pendingRequestId && pendingRequestId === payload.request_id) {
                    console.log('[SessionRevokedModal] Ignoring SSA_LOGIN_REQUEST — this window is the requester.');
                    return;
                }
                const ttl = payload.timeout || 30;
                setSsaRequestId(payload.request_id);
                setSsaDeviceInfo(payload.device_info || 'Unknown Device');
                setSsaIpAddress(payload.ip_address || 'Unknown IP');
                setSsaCountry(payload.country_name || '');
                setSsaCity(payload.city || '');
                setModalType('SSA_REQUEST');
                setIsProcessing(false);
                setMaxTimeout(ttl);
                setCountdown(ttl);
                setIsOpen(true);
            }, 500);
        });

        const unsubRevoke = subscribe('SESSION_REVOKED', (payload) => {
            const revokedSid = payload?.revoked_sid;
            // Only show modal if the revocation explicitly targets THIS session.
            if (revokedSid && revokedSid === sid) {
                setModalType('SESSION_REVOKED');
                setMaxTimeout(15);
                setCountdown(15);
                setIsOpen(true);
            }
        });

        return () => {
            unsubSSA();
            unsubRevoke();
        };
    }, [token, user, sid, subscribe]);

    // ── Local event listeners ───────────────────────────────────────────────
    useEffect(() => {
        const handleSessionRevoked = (e) => {
            // If the event carries an explicit sid that differs from ours,
            // it belongs to a different tab/session — ignore it.
            const targetSid = e?.detail?.sid;
            if (targetSid && sid && targetSid !== sid) return;
            setModalType('SESSION_REVOKED');
            setMaxTimeout(15);
            setCountdown(15);
            setIsOpen(true);
        };
        window.addEventListener('session-revoked', handleSessionRevoked);
        return () => window.removeEventListener('session-revoked', handleSessionRevoked);
    }, [sid]);

    // ── One-shot SSA check on mount (WS handles ongoing delivery) ──────────
    // The WebSocket subscription above (SSA_LOGIN_REQUEST) handles real-time
    // delivery. This one-shot fetch only catches a pending SSA request that
    // existed BEFORE the WS connection was established (e.g. page refresh
    // while a request was in-flight). No interval — no polling.
    useEffect(() => {
        if (!token || !user) return;
        let cancelled = false;

        const checkOnce = async () => {
            try {
                const res = await apiGet('/auth/sessions/ssa-alerts');
                if (cancelled || !res.has_alert) return;
                const pendingRequestId = sessionStorage.getItem('ssa_pending_request');
                if (pendingRequestId && pendingRequestId === res.request_id) return;
                if (ssaRequestId === res.request_id) return; // already shown

                setSsaRequestId(res.request_id);
                setSsaDeviceInfo(res.device_info || 'Unknown Device');
                setSsaIpAddress(res.ip_address || 'Unknown IP');
                setModalType('SSA_REQUEST');
                setIsProcessing(false);
                setMaxTimeout(res.timeout || 30);
                setCountdown(res.timeout || 30);
                setIsOpen(true);
            } catch {
                // Silent — WS is primary delivery mechanism
            }
        };

        checkOnce();
        return () => { cancelled = true; };
    // Only on mount / user identity change — NOT on isOpen/modalType/ssaRequestId
    // to avoid re-running (and re-fetching) on every state change.
    }, [token, user]); // eslint-disable-line react-hooks/exhaustive-deps


    // ── SSA/SESSION_REVOKED countdown (unchanged logic) ─────────────────────
    useEffect(() => {
        if (!isOpen || isProcessing) return;
        if (countdown <= 0) {
            if (modalType === 'SESSION_REVOKED') logout();
            else if (modalType === 'SSA_REQUEST' && ssaRequestId) handleSsaReject();
            setIsOpen(false);
            return;
        }
        const t = setInterval(() => setCountdown(p => p - 1), 1000);
        return () => clearInterval(t);
    }, [isOpen, countdown, isProcessing, modalType, logout, ssaRequestId]);

    // ── SSA handlers (unchanged) ────────────────────────────────────────────
    const handleSsaApprove = async () => {
        setIsProcessing(true);
        try {
            await apiPost(`/auth/sessions/ssa-approve/${ssaRequestId}`, tokenRef.current, {});
            setIsOpen(false);
            logout();
        } catch {
            setIsProcessing(false);
            setIsOpen(false);
        }
    };

    const handleSsaReject = async () => {
        setIsProcessing(true);
        try {
            await apiPost(`/auth/sessions/ssa-reject/${ssaRequestId}`, tokenRef.current, {});
            setIsOpen(false);
        } catch (err) {
            if (err.message?.includes("Session terminated")) {
                // The interceptor already dispatched session-revoked. Do not force close.
            } else {
                setIsOpen(false);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const pct = isOpen ? Math.max(0, (countdown / (maxTimeout || 10)) * 100) : 0;
    const location = [ssaCity, ssaCountry].filter(Boolean).join(', ');

    return (
        <>
            {/* ── SSA / SESSION_REVOKED blocking modal (existing, unchanged) ── */}
            {isOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in">

                        {/* Header */}
                        <div className={`px-5 py-4 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 ${
                            modalType === 'SSA_REQUEST' ? 'bg-amber-50 dark:bg-amber-900/20' :
                            'bg-red-50 dark:bg-red-900/20'
                        }`}>
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${
                                modalType === 'SSA_REQUEST' ? 'bg-amber-100 dark:bg-amber-800/40' :
                                'bg-red-100 dark:bg-red-800/40'
                            }`}>
                                {modalType === 'SSA_REQUEST' ? '🔐' : '⚠️'}
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {modalType === 'SSA_REQUEST' ? 'New Login Request' :
                                     'Session Terminated'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {modalType === 'SSA_REQUEST' ? 'Someone is trying to access your account' :
                                     'Your session was ended remotely'}
                                </p>
                            </div>
                        </div>

                        <div className="px-5 py-6">
                            <div className="space-y-4">
                                {modalType === 'SSA_REQUEST' && (
                                    <div className="rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 px-4 py-3 space-y-1.5 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">IP Address</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200">{ssaIpAddress}</span>
                                        </div>
                                        {location && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 dark:text-gray-400">Location</span>
                                                <span className="text-gray-800 dark:text-gray-200">{location}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {modalType === 'SSA_REQUEST' && (
                                    <div className="flex justify-center">
                                        <CountdownRing value={pct} seconds={countdown} isSSA={modalType === 'SSA_REQUEST'} />
                                    </div>
                                )}
                                {modalType === 'SSA_REQUEST' && (
                                    <p className="text-center text-[11px] text-gray-500 dark:text-gray-400 !mt-3 px-4 italic leading-relaxed">
                                        If no action is taken, this request will be <strong className="font-medium text-amber-600 dark:text-amber-500">auto-approved</strong> when the timer expires.
                                    </p>
                                )}
                                {isProcessing && (
                                    <div className="h-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                        <div className="h-full w-1/3 bg-purple-500 rounded-full animate-[shimmer_1s_linear_infinite]" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="px-5 pb-5">
                            {modalType === 'SSA_REQUEST' ? (
                                <div className="flex gap-3">
                                    <button onClick={handleSsaReject} disabled={isProcessing}
                                        className="flex-1 py-2.5 text-sm font-semibold rounded-xl border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors">
                                        Block
                                    </button>
                                    <button onClick={handleSsaApprove} disabled={isProcessing}
                                        className="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-primary hover:bg-primary-dark text-white disabled:opacity-50 shadow-md shadow-primary/30 transition-all">
                                        Allow Login
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    <button onClick={logout}
                                        className="w-full py-2.5 text-sm font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white transition-colors">
                                        Log out now
                                    </button>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="w-full py-2 text-xs font-medium text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                        Dismiss — I'm still here
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// SVG countdown ring — unchanged
const CountdownRing = ({ value, seconds, isSSA }) => {
    const r = 22;
    const circ = 2 * Math.PI * r;
    const color = isSSA ? '#f59e0b' : '#ef4444';

    return (
        <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
            <svg width="56" height="56">
                <circle cx="28" cy="28" r={r} fill="none" stroke="currentColor"
                    className="text-gray-200 dark:text-gray-700" strokeWidth="4" />
                <circle cx="28" cy="28" r={r} fill="none"
                    stroke={color}
                    strokeWidth="4"
                    strokeDasharray={circ}
                    strokeDashoffset={circ * (1 - value / 100)}
                    strokeLinecap="round"
                    transform="rotate(-90 28 28)"
                    style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
            </svg>
            <span style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '14px', fontWeight: 700, color,
                lineHeight: 1,
            }}>
                {seconds}
            </span>
        </div>
    );
};

export default SessionRevokedModal;
